import { Suspense, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Center, Loader } from '@mantine/core';
import {
	DocumentWorkflowTemplateSourceFormat,
	GlobalDocumentTemplateRes,
} from '@dealstack/shared/lib/generated';
import { FieldBindingEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/FieldBindingEditor';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useGlobalFieldBindingPreview } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useGlobalFieldBindingPreview';
import { useUpdateDocumentTemplate } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useUpdateDocumentTemplate';
import { TemplateNameField } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/TemplateNameField';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types';
import { globalDocxFlowAdapter } from './globalDocxFlowAdapter';
import { useGlobalFieldBinding } from './useGlobalFieldBinding';
import { useGlobalTemplateFile } from './useGlobalTemplateFile';

interface Props {
	template: GlobalDocumentTemplateRes;
}

export const StaticPdfTemplateEditor = ({ template }: Props) => {
	const navigate = useNavigate();
	const tDefault = useTranslationDefault();
	const binding = useGlobalFieldBinding(template.id);
	// A Word template edits its converted document, not the uploaded bytes.
	const isDocx = template.sourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX;
	const { data: file, isPending, isError } = useGlobalTemplateFile(template.fileId, !isDocx);
	const updateTemplateRequest = useUpdateDocumentTemplate();
	// Local echo of the stored name. The parent keys this editor by templateId, so it never
	// goes stale across templates.
	const [nameDraft, setNameDraft] = useState(template.name);

	const previewMutation = useGlobalFieldBindingPreview();
	// No deal, so no investor to render for — always renders the sample dataset.
	const requestPreview = useCallback(
		(_investorId: string | null) => previewMutation.mutateAsync(template.id),
		[template.id, previewMutation]
	);

	// Leaving the field only tidies it. An emptied name reverts rather than being kept: the
	// column is NOT NULL and a blank title is never what was meant.
	const normalizeName = () => {
		const trimmed = nameDraft.trim();
		setNameDraft(trimmed || template.name);
	};

	// The panel's Save is what writes the name, alongside the field bindings it already saves.
	// A name edit is unsaved work like any other, so it belongs to the one footer that reports
	// whether there is any, rather than committing on blur behind that footer's back.
	const trimmedName = nameDraft.trim();
	const nameEdit: PanelHeaderEdit = {
		isDirty: trimmedName.length > 0 && trimmedName !== template.name,
		save: async () => {
			await updateTemplateRequest.mutateAsync({
				id: template.id,
				body: { name: trimmedName },
			});
		},
		discard: () => setNameDraft(template.name),
		isSaving: updateTemplateRequest.isPending,
	};

	if (!isDocx && isPending) {
		return (
			<Center h="60vh">
				<Loader />
			</Center>
		);
	}

	// Distinguished from loading: a failed fetch would otherwise sit on the spinner forever
	// with no way to tell that it is never resolving.
	if (!isDocx && (isError || !file)) {
		return (
			<Alert color="red" m="md">
				{tDefault('document_templates.static_pdf.file_unavailable')}
			</Alert>
		);
	}

	return (
		// Local Suspense boundary: every string in FieldBindingEditor comes from the
		// `mip-manage` namespace, which /templates does not otherwise load. Without this the
		// lazy fetch suspends to the app-level boundary and blanks the whole shell.
		<Suspense fallback={null}>
			<FieldBindingEditor
				templateId={template.id}
				file={file ?? ''}
				// A Word template gets the document itself as the editing surface; a PDF keeps
				// the coordinate canvas. The adapter is what makes that surface deal-less.
				sourceFormat={template.sourceFormat}
				docxAdapter={globalDocxFlowAdapter}
				binding={binding}
				requestPreview={requestPreview}
				// Above the per-field panel rather than inside it, so the template's name is
				// on screen whether or not a blank is selected.
				panelHeader={
					<TemplateNameField
						value={nameDraft}
						onChange={setNameDraft}
						onBlur={normalizeName}
					/>
				}
				panelHeaderEdit={nameEdit}
				onClose={() => navigate(ROUTES.TEMPLATES)}
			/>
		</Suspense>
	);
};
